import type { CheckoutSessionResponse, CustomerSubscriptionsResponse, DonationSessionResponse, PaymentIntentDto, ReaderDto, ReaderListDto, StripePaymentDto, StripePaymentSearchDto } from './dtos/models';
import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import type { PaymentRequestVm } from '../payment-requests/models';
import type { CreateKioskPaymentDto } from '../terminal-payments/models';

@Injectable({
  providedIn: 'root',
})
export class StripeService {
  apiName = 'Default';
  

  cancelTerminalPaymentIntent = (paymentIntentId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PaymentIntentDto>({
      method: 'POST',
      url: `/api/app/stripe/terminal/cancel-payment-intent/${paymentIntentId}`,
    },
    { apiName: this.apiName,...config });
  

  confirmTerminalPaymentIntent = (paymentIntentId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PaymentIntentDto>({
      method: 'POST',
      url: `/api/app/stripe/terminal/confirm-payment-intent/${paymentIntentId}`,
    },
    { apiName: this.apiName,...config });
  

  createCheckoutSessionByAmountAndCurrencyAndEmailAndNameAndSuccessUrlAndCancelUrlAndCommentsAndPaymentSessionIdAndNumberOfTicketsAndTotalAmountAndPrimaryTypeIdAndSecondaryTypeIdAndPaymentCategoryAndPaymentTypeId = (amount: number, currency: string, email?: string, name?: string, successUrl?: string, cancelUrl?: string, Comments?: string, PaymentSessionId?: string, NumberOfTickets?: number, TotalAmount?: string, PrimaryTypeId?: string, SecondaryTypeId?: string, PaymentCategory?: string, PaymentTypeId?: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string>({
      method: 'GET',
      responseType: 'text',
      url: '/api/app/stripe/create-checkout-session',
      params: { amount, currency, email, name, successUrl, cancelUrl, comments: Comments, paymentSessionId: PaymentSessionId, numberOfTickets: NumberOfTickets, totalAmount: TotalAmount, primaryTypeId: PrimaryTypeId, secondaryTypeId: SecondaryTypeId, paymentCategory: PaymentCategory, paymentTypeId: PaymentTypeId },
    },
    { apiName: this.apiName,...config });
  

  createConnectionToken = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, object>({
      method: 'POST',
      url: '/api/app/stripe/terminal/connection-token',
    },
    { apiName: this.apiName,...config });
  

  createCustomerBillingPortalSessionByCustomerIdAndReturnUrl = (customerId: string, returnUrl?: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string>({
      method: 'POST',
      responseType: 'text',
      url: '/api/app/stripe/create-billing-portal-session',
      params: { customerId, returnUrl },
    },
    { apiName: this.apiName,...config });
  

  createCustomerPortalSessionByCustomerId = (customerId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string>({
      method: 'POST',
      responseType: 'text',
      url: '/api/app/stripe/create-customer-portal-session',
      params: { customerId },
    },
    { apiName: this.apiName,...config });
  

  createDonationCheckoutSessionByInput = (input: PaymentRequestVm, config?: Partial<Rest.Config>) =>
    this.restService.request<any, DonationSessionResponse>({
      method: 'POST',
      url: '/api/app/stripe/create-donation-checkout-session',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createSimulatedReaderByLocationIdAndRegistrationCode = (locationId: string, registrationCode: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ReaderDto>({
      method: 'POST',
      url: '/api/app/stripe/terminal/create-simulated-reader',
      params: { locationId, registrationCode },
    },
    { apiName: this.apiName,...config });
  

  createTerminalPaymentIntentForKiosk = (input: CreateKioskPaymentDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PaymentIntentDto>({
      method: 'POST',
      url: '/api/app/stripe/terminal/create-kiosk-payment-intent',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  discoverReaders = (locationId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ReaderListDto>({
      method: 'POST',
      url: '/api/app/stripe/terminal/discover-readers',
      params: { locationId },
    },
    { apiName: this.apiName,...config });
  

  getCustomerSubscriptions = (email: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CustomerSubscriptionsResponse>({
      method: 'GET',
      url: '/api/app/stripe/get-customer-subscriptions',
      params: { email },
    },
    { apiName: this.apiName,...config });
  

  getPayments = (input: StripePaymentSearchDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<StripePaymentDto>>({
      method: 'POST',
      url: '/api/app/stripe/GetPayments',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  getStripeIntentByAmountAndCurrencyAndEmailAndName = (amount: number, currency: string, email?: string, name?: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string>({
      method: 'GET',
      responseType: 'text',
      url: '/api/app/stripe/GetStripeIntent',
      params: { amount, currency, email, name },
    },
    { apiName: this.apiName,...config });
  

  getStripeIntentStatusByPaymentIntentId = (paymentIntentId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string>({
      method: 'GET',
      responseType: 'text',
      url: '/api/app/stripe/GetStripeIntentStatus',
      params: { paymentIntentId },
    },
    { apiName: this.apiName,...config });
  

  getStripeIntentsByEmail = (email: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string>({
      method: 'GET',
      responseType: 'text',
      url: '/api/app/stripe/GetStripeIntents',
      params: { email },
    },
    { apiName: this.apiName,...config });
  

  getTerminalPaymentIntent = (paymentIntentId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PaymentIntentDto>({
      method: 'GET',
      url: `/api/app/stripe/terminal/payment-intent/${paymentIntentId}`,
    },
    { apiName: this.apiName,...config });
  

  getUnprocessedPayments = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, StripePaymentDto[]>({
      method: 'GET',
      url: '/api/app/stripe/GetUnprocessedPayments',
    },
    { apiName: this.apiName,...config });
  

  handleWebhook = (json: string, stripeSignature: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: '/api/app/stripe/HandleWebhook',
      params: { json, stripeSignature },
    },
    { apiName: this.apiName,...config });
  

  processPayment = (id: string, category: string, notes: string, customerEmail?: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StripePaymentDto>({
      method: 'POST',
      url: '/api/app/stripe/ProcessPayment',
      params: { id, category, notes, customerEmail },
    },
    { apiName: this.apiName,...config });
  

  processTerminalPaymentIntent = (paymentIntentId: string, terminalId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ReaderDto>({
      method: 'POST',
      url: `/api/app/stripe/terminal/process-payment-intent/${paymentIntentId}/${terminalId}`,
    },
    { apiName: this.apiName,...config });
  

  retrieveCheckoutSessionBySessionId = (sessionId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CheckoutSessionResponse>({
      method: 'GET',
      url: '/api/app/stripe/retrieve-checkout-session',
      params: { sessionId },
    },
    { apiName: this.apiName,...config });
  

  simulateTerminalPaymentByTerminalId = (terminalId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, boolean>({
      method: 'POST',
      url: `/api/app/stripe/terminal/simulate-terminal-payment/${terminalId}`,
    },
    { apiName: this.apiName,...config });

  constructor(private restService: RestService) {}
}
